import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = this.closest('product-card');
    const hotspotProduct = this.closest('product-hotspot-component');
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();
    if (!productLink?.href) return '';
    const url = new URL(productLink.href);
    if (url.searchParams.has('variant')) return url.toString();
    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);
    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard = this.closest('product-card');
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#cartUpdateAbortController = new AbortController();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const variantPicker = modalContent?.querySelector('variant-picker');
    if (variantPicker) variantPicker.updateVariantPicker(newHtml);
  }

  handleClick = async (event) => {
    event.preventDefault();
    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    try {
      let productGrid = this.#cachedContent.get(currentUrl);
      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl);
        if (!html) return;
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = gridElement.cloneNode(true);
          this.#cachedContent.set(currentUrl, productGrid);
        } else {
          // La ficha pdp-main utiliza un formulario propio.
          this.#renderCustomProduct(html, currentUrl);
          return;
        }
      }
      const freshContent = productGrid.cloneNode(true);
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
    } catch (error) {
      console.error('Quick add: unable to load product.', error);
    }
  };

  #renderCustomProduct(html, currentUrl) {
    const source = html.querySelector('[data-pdp]');
    const sourceForm = source?.querySelector('form');
    const variantsData = source?.querySelector('[data-pdp-variants]');
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!source || !sourceForm || !variantsData || !modalContent) {
      throw new Error('Quick add: custom product content is missing.');
    }
    const variants = JSON.parse(variantsData.textContent);
    if (!Array.isArray(variants) || !variants.length) {
      throw new Error('Quick add: invalid variant data.');
    }

    const panel = document.createElement('div');
    panel.className = 'pdp av-quick-panel';
    const styles = document.createElement('style');
    styles.textContent = `
      #quick-add-dialog .quick-add-modal:has(.av-quick-panel) {
        background: #fff; color: #171717;
        width: min(100%, 520px); height: auto;
        max-height: 90vh; max-height: 90dvh;
        border: 1px solid #e8e6e3; border-radius: 20px;
      }
      #quick-add-dialog .quick-add-modal:has(.av-quick-panel) .quick-add-modal__close {
        color: #171717; background: #fff; border-radius: 50%;
        top: 12px; right: 12px; width: 40px; height: 40px;
      }
      #quick-add-dialog .quick-add-modal__content:has(.av-quick-panel) {
        display: block; padding: 0; overflow-y: auto; max-height: 88vh; max-height: 88dvh;
      }
      #quick-add-dialog .av-quick-panel {
        --pdp-ink: #171717; --pdp-line: #e8e6e3;
        width: 100%; min-width: 0; box-sizing: border-box;
        padding: 48px 24px calc(24px + env(safe-area-inset-bottom));
        background: #fff; color: #171717; font-family: inherit;
      }
      #quick-add-dialog .av-quick-header {
        display: grid; grid-template-columns: 112px minmax(0, 1fr);
        gap: 20px; align-items: center; margin-bottom: 26px;
      }
      #quick-add-dialog .av-quick-image {
        display: block; width: 112px; height: 144px;
        object-fit: contain; background: #f6f5f3;
        border: 1px solid #eeece9; border-radius: 12px;
      }
      #quick-add-dialog .av-quick-title {
        display: block; color: inherit; text-decoration: none;
        font-size: 15px; font-weight: 500; line-height: 1.5;
        letter-spacing: .025em; overflow-wrap: anywhere;
      }
      #quick-add-dialog .av-quick-price {
        margin: 10px 0 0; font-size: 16px; font-weight: 500;
        font-variant-numeric: tabular-nums;
      }
      #quick-add-dialog .av-quick-panel .pdp__label {
        margin-bottom: 9px; font-size: 12px; font-weight: 500; letter-spacing: .06em;
      }
      #quick-add-dialog .av-quick-panel .pdp__select {
        background: #fff; color: #171717; border: 1px solid #dedbd6;
        border-radius: 10px; box-shadow: none; height: 48px; font-size: 14px;
      }
      #quick-add-dialog .av-quick-panel .pdp__buy {
        display: grid; grid-template-columns: 124px minmax(0, 1fr);
        align-items: center; gap: 12px; margin: 22px 0 0;
      }
      #quick-add-dialog .av-quick-panel .pdp__qty {
        display: flex; width: 124px; height: 48px; box-sizing: border-box;
        background: #fff; border: 1px solid #dedbd6; border-radius: 10px; box-shadow: none;
      }
      #quick-add-dialog .av-quick-panel .pdp__qty-btn {
        flex: 0 0 38px; width: 38px; height: 46px; padding: 0;
        background: transparent; color: #171717; border: 0; box-shadow: none;
      }
      #quick-add-dialog .av-quick-panel input.pdp__qty-input {
        background: transparent !important; color: #171717 !important;
        -webkit-text-fill-color: #171717 !important; opacity: 1;
        border: 0 !important; box-shadow: none !important;
        width: 44px; min-width: 0; padding: 0; height: 44px;
        text-align: center; font: inherit; font-size: 14px;
      }
      #quick-add-dialog .av-quick-panel .pdp__atc {
        width: 100%; min-width: 0; height: 48px; padding: 0 12px;
        background: #171717; color: #fff; border: 1px solid #171717;
        border-radius: 10px; box-shadow: none; font-size: 10px;
        font-weight: 600; letter-spacing: .1em;
      }
      #quick-add-dialog .av-quick-panel .pdp__atc:disabled { opacity: .45; }
      #quick-add-dialog .av-quick-panel :is(button, input, select, a):focus-visible {
        outline: 2px solid #766653; outline-offset: 3px;
      }
      @media (max-width: 749px) {
        #quick-add-dialog .quick-add-modal:has(.av-quick-panel) {
          margin: auto 0 0; border-radius: 20px 20px 0 0;
        }
      }
      @media (max-width: 350px) {
        #quick-add-dialog .av-quick-panel { padding-inline: 18px; }
        #quick-add-dialog .av-quick-header { grid-template-columns: 88px minmax(0, 1fr); gap: 14px; }
        #quick-add-dialog .av-quick-image { width: 88px; height: 116px; }
        #quick-add-dialog .av-quick-panel .pdp__buy { grid-template-columns: 1fr; }
      }
    `;

    const title = document.createElement('a');
    title.href = currentUrl;
    title.textContent = source.querySelector('.pdp__title')?.textContent.trim() || this.dataset.productTitle || '';
    title.className = 'av-quick-title';

    const price = document.createElement('p');
    price.className = 'av-quick-price';
    const header = document.createElement('div');
    header.className = 'av-quick-header';
    const information = document.createElement('div');
    information.append(title, price);
    const image = document.createElement('img');
    image.className = 'av-quick-image';
    image.alt = title.textContent;
    image.width = 112;
    image.height = 144;
    const defaultImage = source.querySelector('[data-slide]:not([hidden]) img') || source.querySelector('.pdp__image');
    const updateImage = (imageId) => {
      const slide = [...source.querySelectorAll('[data-slide]')].find((item) => String(item.dataset.slide) === String(imageId));
      const sourceImage = slide?.querySelector('img') || defaultImage;
      if (!sourceImage) {
        image.hidden = true;
        header.style.gridTemplateColumns = '1fr';
        return;
      }
      image.hidden = false;
      image.src = sourceImage.getAttribute('src') || '';
      image.alt = sourceImage.getAttribute('alt') || title.textContent;
      const srcset = sourceImage.getAttribute('srcset');
      if (srcset) image.srcset = srcset;
      else image.removeAttribute('srcset');
      image.sizes = '112px';
    };
    updateImage(null);
    header.append(image, information);
    const form = sourceForm.cloneNode(true);
    form.removeAttribute('id');
    form.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));

    const selects = [...form.querySelectorAll('[data-pdp-option]')];
    const idInput = form.querySelector('[data-pdp-id]');
    const addButton = form.querySelector('[data-pdp-atc]');
    const quantity = form.querySelector('[data-pdp-qty-input]');
    if (!idInput || !addButton) throw new Error('Quick add: incomplete product form.');

    selects.forEach((select, index) => {
      select.id = `quick-add-pdp-option-${index}`;
      const label = select.closest('.pdp__field')?.querySelector('label');
      if (label) label.htmlFor = select.id;
    });

    const addLabel = source.dataset.addLabel || 'Add to cart';
    const soldLabel = source.dataset.soldLabel || 'Sold out';
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.style.cssText = 'margin:0;font-size:13px;';
    status.hidden = true;

    const updateVariant = () => {
      const values = selects.map((select) => select.value);
      const variant = variants.find((item) => selects.length
        ? values.every((value, index) => item.options[index] === value)
        : String(item.id) === String(idInput.value));
      idInput.value = variant ? String(variant.id) : '';
      addButton.disabled = !variant?.available;
      addButton.textContent = variant?.available ? addLabel : soldLabel;
      price.textContent = variant?.price || '';
      updateImage(variant?.image);
      status.hidden = Boolean(variant?.available);
      status.textContent = soldLabel;
    };

    selects.forEach((select) => select.addEventListener('change', updateVariant));
    form.querySelectorAll('[data-pdp-qty]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!quantity) return;
        const current = parseInt(quantity.value, 10) || 1;
        const change = parseInt(button.dataset.pdpQty, 10) || 0;
        quantity.value = String(Math.max(1, current + change));
      });
    });
    form.addEventListener('submit', (event) => {
      updateVariant();
      if (!idInput.value || addButton.disabled) event.preventDefault();
    });

    panel.append(styles, header, form, status);
    modalContent.replaceChildren(panel);
    updateVariant();
    this.#openQuickAddModal();
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    dialogComponent.querySelector('.product-details')?.scrollTo({ top: 0, behavior: 'instant' });
    dialogComponent.querySelector('.product-information__media')?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    try {
      const response = await fetch(productPageUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      if (this.#abortController === controller) this.#abortController = null;
    }
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;
    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;
      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      if (productPrice) productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      if (variantPicker) productGrid.appendChild(variantPicker);
      if (productFormComponent) productGrid.appendChild(productFormComponent);
      productDetails?.remove();
    }
    morph(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);
  }

  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const quickAddButton = this.dataset.productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, { signal: this.#abortController.signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (event) => {
    const anchorElement = event.detail.data.html?.querySelector('.view-product-title a');
    const viewMoreDetailsLink = this.querySelector('.view-product-title a');
    const mobileProductTitle = this.querySelector('.product-header a');
    if (!anchorElement) return;
    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;
    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => { grid.style.width = ''; });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

